import type { ReactElement } from 'react';
import { useMemo } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import { useTranslation } from 'react-i18next';
import { NavLink, matchPath, useLocation } from 'react-router';
import styled, { css } from 'styled-components';

import { AccordionDetails, AccordionSummary, Box } from '@mui/material';
import {
  CaretRightIcon,
  Warning,
} from '@phosphor-icons/react';

import type { NavItem } from '../../../config/navigation';
import { isEmpty } from '../../../services/checks';
import { isNotNil } from '../../../services/guards';
import useFocusWithin from '../../../services/hooks/useFocusWithin';
import type { Nullable } from '../../../services/object';
import palette from '../../../theme/palette';

import Accordion from '../../atoms/Accordion';
import type { NavButtonBaseProps } from '../../atoms/NavButtonBase';
import NavButtonBase from '../../atoms/NavButtonBase';
import Rotate from '../../atoms/Rotate';
import Rows from '../../atoms/Rows';
import NewFeatureHighlight from '../../organisms/NewFeatureHighlight';
import Badge from '../Badge';
import Body from '../Body';
import Counter from '../Counter';
import Header from '../Header';
import Icon from '../Icon';
import TextGroup from '../TextGroup';

type StyledNavLinkProps = NavButtonBaseProps & Readonly<{
  active: boolean;
  level: number;
  muted?: boolean;
}>;

const changeOnActiveStyle = ({ active, level }: StyledNavLinkProps) => {
  if (active) {
    return css`
      &,
      &:hover,
      &:focus-within {
        background-color: ${({ theme }) => [
          theme.scTheme.palette.primary[60],
          theme.scTheme.palette.primary[10],
        ][level] ?? theme.scTheme.palette.primary[60]};
      }
    `;
  }
  return null;
};

const getColors = ({ active, level }: StyledNavLinkProps) => {
  if (!active) {
    return {
      icon: {
        variant: 'secondary',
        inverted: false,
      },
      text: {
        variant: 'primary',
        inverted: false
      },
      warningIcon: {
        fill: palette.support.warning[50]
      },
      expansionCaret: {
        fill: palette.greyscale[60]
      }
    } as const
  }

  if (level === 0) {
    return {
      icon: {
        variant: 'primary',
        inverted: true,
      },
      text: {
        variant: 'primary',
        inverted: true
      },
      warningIcon: {
        fill: palette.inverted.white
      },
      expansionCaret: {
        fill: palette.inverted.white
      }
    } as const
  }

  return {
    icon: {
      variant: 'primary',
      inverted: false,
    },
    text: {
      variant: 'primary',
      inverted: false
    },
    warningIcon: {
      fill: palette.support.warning[50]
    },
    expansionCaret: {
      fill: palette.greyscale[60]
    }
  } as const
}

const StyledLink = styled(NavButtonBase).withConfig({
  shouldForwardProp,
})<StyledNavLinkProps>`
  ${changeOnActiveStyle};
  opacity: ${({ muted }) => (muted ? 0.5 : null)};
`;

// Icon renders its children inside an <svg>, so an avatar cannot go through the icon slot.
const getLeadingVisual = (item: NavItem): Nullable<ReactElement> => {
  const visual = item.avatar ?? (isNotNil(item.icon) ? <Icon size="lg">{item.icon}</Icon> : null);

  return isNil(visual) ? null : (
    <Box display="flex" mr={600}>
      {visual}
    </Box>
  );
};

const pathMatchesItem = (item: NavItem, pathname: string) => Boolean(matchPath(item.url, pathname))
    || (!isEmpty(item.associatedRoutes) && item.associatedRoutes.some((route) => matchPath(route, pathname)))

export type Props = Readonly<{
  item: NavItem;
  changeOnActive?: boolean;
  level?: number;
  onNavigate?: (item: NavItem) => void;
}>;

const NavButton = ({
  item, changeOnActive = false, level = 0, onNavigate = noop,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const location = useLocation();

  const [focusRef, hasFocusWithin] = useFocusWithin<HTMLDivElement>();

  const isActive = useMemo(() => pathMatchesItem(item, location.pathname)
    || (item.subItems ?? []).some((subItem) => pathMatchesItem(subItem, location.pathname)),
  [location.pathname, item]);

  const applyActiveStyling = isActive && changeOnActive;

  const disabled = item.disabled ?? false;
  const muted = (item.muted ?? false) && !applyActiveStyling;
  const expandable = !isEmpty(item.subItems)
  const warning = (item.warning ?? false) && !(expandable && isActive);

  const colors = getColors({ active: applyActiveStyling, level })

  const body = (
    <>
      <Box display="flex" alignItems="center">
        <Body size={300} {...colors.icon} disabled={disabled}>
          {getLeadingVisual(item)}
        </Body>
        <TextGroup hideOverflow>
          {level !== 0 && applyActiveStyling ? (
            <Header as="h5" {...colors.text} disabled={disabled}>
              <Box display="inline-flex" alignItems="center" justifyContent="flex-start">
                {item.title}
              </Box>
            </Header>
          ) : (
            <Body size={300} {...colors.text} disabled={disabled}>
              {item.title}

            </Body>
          )}
        </TextGroup>
        {!isNil(item.counter) && <Box ml={300} display="flex">
          <Counter type={applyActiveStyling ? 'increase' : 'neutral'} inverted={applyActiveStyling} value={item.counter} variant="subtle" />
        </Box>}
        {warning && (
          <Box display="flex" ml="auto" mr={300}>
            <Icon size="md">
              <Warning weight="fill" {...colors.warningIcon} />
            </Icon>
          </Box>
        )}
        <Box display="flex" ml={warning ? undefined : 'auto'} mr={300} justifyContent="center" visibility={expandable ? undefined : 'hidden'}>
          <Rotate degrees={90} isRotated={isActive}>
            <Icon size="sm">
              <CaretRightIcon {...colors.expansionCaret} />
            </Icon>
          </Rotate>
        </Box>
      </Box>
      {item.caption}
    </>
  );

  if (disabled) {
    return (
      <NavButtonBase disabled>
        <Box display="flex">
          {body}
          <Box ml={400}>
            <Badge>
              {t('Soon')}
            </Badge>
          </Box>
        </Box>

      </NavButtonBase>
    );
  }

  const link = (
    <StyledLink
      level={level}
      active={applyActiveStyling}
      muted={muted}
      as={NavLink}
      to={item.url}
      onClick={() => onNavigate(item)}
    >
      {body}
    </StyledLink>
  );

  const summary = item.newFeature === true ? (
    <NewFeatureHighlight featureKey={item.newFeatureKey ?? `${item.id}-subpage`}>
      {link}
    </NewFeatureHighlight>
  ) : link;

  return (
    <Accordion
      expanded={isActive || hasFocusWithin}
      ref={focusRef}
      summaryClickable={false}
    >
      <AccordionSummary tabIndex={-1} slotProps={{ root: { component: 'div' } }}>
        {summary}
      </AccordionSummary>
      {expandable && (
        <AccordionDetails>
          <Box mt={200}>
            <Rows gap={200}>
              {item.subItems?.map((entry) => <NavButton
                key={entry.id}
                level={level + 1}
                item={entry}
                changeOnActive={changeOnActive}
                onNavigate={onNavigate}
              />)}
            </Rows>
          </Box>
        </AccordionDetails>
      )}

    </Accordion>

  );
};
export default NavButton;
